import {
  FFS_ENTRY_FIELD,
  FFS_OUTCOME_FIELD,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsEntryFields'
import {
  FfsLevel,
  FfsPart,
  FfsResultType,
} from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import {
  GeckoEntry,
  GeckoMeasurement,
  GeckoMeasurementJson,
  RolodexEntryType,
} from 'types'

import { ScenarioKey, scenarioKeyOf } from './ffsScenarioKey'
import { isFfsResultType } from './ffsSeverity'

/**
 * A NARROW, fail-soft projection of an `ffs-lta-report` Rolodex entry — the only
 * shape the close-out rollup reads.
 *
 * Deliberately not `fromRolodexEntry` (`ffsLtaPublishApi.ts`): that runs the full
 * Zod schema plus `createFfsStrategy` per entry and THROWS on legacy or
 * malformed data. Close-out reads every report on a whole unit at once, so one
 * bad row would take down the entire sign-off view. Here a bad row degrades to
 * `{ ok: false }` and is attributed to its LTA instead (see `disposeLta`), which
 * blocks that one LTA rather than silently vanishing — a dropped sibling could
 * otherwise leave a stale Pass looking like the governing report.
 */
export interface FfsReportRecord {
  id: string
  indicationId: string
  part: FfsPart
  level: FfsLevel
  result: FfsResultType
  /** Read only to detect (never to write) divergence from the engineer's flag. */
  isDefault: boolean
  /** `null` when the report carries neither a target date nor a usable label. */
  scenarioKey: ScenarioKey | null
  mawpr: GeckoMeasurement | null
  /** Entry `updated_at`; drives duplicate tie-breaks only (mutable — `is_default`
   * patches bump it, so it must NOT gate staleness). */
  updatedAt: string
  /**
   * IMMUTABLE calc timestamp, written once at publish and never patched — the
   * staleness + cross-cell comparability gate (`computedAt >= classification
   * .decidedAt`). `null` on legacy rows saved before the field existed; those are
   * fail-closed (excluded from governing, surfaced as `needs-resave`) rather than
   * trusted via the mutable `updatedAt`.
   */
  computedAt: string | null
}

/**
 * A parse that failed still reports which LTA it belonged to when the tag is
 * readable, so the failure can be attributed rather than dropped. `null` means
 * even that was unavailable — those block at unit level.
 */
export type ParseOutcome =
  | { ok: true; summary: FfsReportRecord }
  | { ok: false; indicationId: string | null }

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null

/**
 * Tag values can be `null` as well as absent, so a plain truthiness check is not
 * enough to narrow to `string`.
 */
function readIndicationTag(entry: GeckoEntry): string | null {
  const value = entry.tags?.[RolodexEntryType.INDICATION]
  return typeof value === 'string' && value ? value : null
}

/**
 * Project one Rolodex entry. Every field is validated; anything unexpected fails
 * the whole row rather than yielding a half-populated summary that would be
 * indistinguishable from real data downstream.
 */
export function parseReportSummary(entry: GeckoEntry): ParseOutcome {
  const indicationId = readIndicationTag(entry)
  const fail: ParseOutcome = { ok: false, indicationId }

  const data: unknown = entry.data
  if (!isRecord(data)) return fail
  if (!indicationId) return fail

  const {
    [FFS_ENTRY_FIELD.ffsPart]: rawPart,
    [FFS_ENTRY_FIELD.ffsLevel]: rawLevel,
    [FFS_ENTRY_FIELD.outcome]: outcome,
    [FFS_ENTRY_FIELD.mawp]: mawp,
    [FFS_ENTRY_FIELD.userParams]: user_params,
    [FFS_ENTRY_FIELD.isDefault]: rawIsDefault,
  } = data

  const part =
    rawPart === FfsPart.PART_4
      ? FfsPart.PART_4
      : rawPart === FfsPart.PART_5
        ? FfsPart.PART_5
        : null
  if (!part) return fail

  const level: FfsLevel | null =
    rawLevel === '1' ? '1' : rawLevel === '2' ? '2' : null
  if (!level) return fail

  if (!isRecord(outcome)) return fail
  const rawResult = outcome['result']
  if (typeof rawResult !== 'string' || !isFfsResultType(rawResult)) {
    return fail
  }
  const result = rawResult

  const scenarioLabel =
    typeof outcome['scenario'] === 'string' ? outcome['scenario'] : null

  // `user_params` and `mawp` are absent on some legacy rows. Their individual
  // fields are optional downstream (a null `mawpr` makes a derate's pressure
  // "unavailable" rather than invalid), so their absence must not fail the row.
  const targetDate =
    isRecord(user_params) && typeof user_params['targetDate'] === 'string'
      ? user_params['targetDate']
      : null

  const mawpRecord = isRecord(mawp) ? mawp : {}
  const mawpr = mawpRecord['mawpr']

  // Immutable calc timestamp; absent on legacy rows (→ `null`, fail-closed later).
  const rawComputedAt = outcome[FFS_OUTCOME_FIELD.computedAt]
  const computedAt = typeof rawComputedAt === 'string' ? rawComputedAt : null

  return {
    ok: true,
    summary: {
      id: entry.id,
      indicationId,
      part,
      level,
      result,
      isDefault: rawIsDefault === true,
      scenarioKey: scenarioKeyOf(targetDate, scenarioLabel),
      mawpr: isRecord(mawpr)
        ? GeckoMeasurement.CreateFromJSON(mawpr as GeckoMeasurementJson)
        : null,
      updatedAt: entry.updated_at,
      computedAt,
    },
  }
}
